import React, { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeftIcon, CalendarDaysIcon, ClockIcon, TagIcon, UserIcon } from '@heroicons/react/24/outline'
import { Blog } from '../../types'
import { blogsApi } from '../../services/api'
import LoadingSpinner from '../../components/common/LoadingSpinner'
import BlogImage from '../../components/common/BlogImage'
import RichTextContent from '../../components/common/RichTextContent'

const formatDate = (value?: string) => {
  if (!value) return 'Not published'
  return new Date(value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
}

const BlogDetailPage: React.FC = () => {
  const { slug } = useParams()
  const [blog, setBlog] = useState<Blog | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    if (!slug) {
      setError('Blog not found.')
      setLoading(false)
      return
    }

    setLoading(true)
    setError('')
    blogsApi.getPublishedBlogBySlug(slug)
      .then((data) => {
        if (mounted) setBlog(data)
      })
      .catch(() => {
        if (mounted) setError('This blog is unavailable or unpublished.')
      })
      .finally(() => {
        if (mounted) setLoading(false)
      })

    return () => { mounted = false }
  }, [slug])

  const tags = useMemo(() => {
    const raw = blog?.tags || ''
    return raw
      .split(',')
      .map(t => t.trim())
      .filter(Boolean)
  }, [blog?.tags])

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (error || !blog) {
    return (
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="rounded-2xl border border-danger-500/30 bg-danger-500/10 p-8 text-center">
          <h1 className="text-2xl font-bold text-white">Blog Not Found</h1>
          <p className="text-sm text-gray-300 mt-2">{error || 'The requested blog could not be found.'}</p>
          <Link to="/blogs" className="inline-flex items-center gap-2 mt-6 text-primary-400 hover:text-primary-300">
            <ArrowLeftIcon className="w-4 h-4" />
            Back to Blogs
          </Link>
        </div>
      </section>
    )
  }

  return (
    <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
      <Link to="/blogs" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
        <ArrowLeftIcon className="w-4 h-4" />
        All Blogs
      </Link>

      <article className="mt-6 rounded-2xl border border-dark-700 bg-dark-900/70 overflow-hidden">
        <div className="aspect-[16/7] w-full bg-dark-800">
          <BlogImage
            src={blog.featured_image}
            alt={blog.title}
            className="w-full h-full object-cover"
            fallbackClassName="w-full h-full flex items-center justify-center text-sm text-gray-500"
            fallbackText="Image unavailable"
          />
        </div>

        <div className="p-6 sm:p-8 lg:p-10">
          <div className="flex flex-wrap items-center gap-2 text-xs mb-3">
            {blog.category && (
              <span className="px-2.5 py-1 rounded-full bg-primary-500/15 text-primary-300 border border-primary-500/30">
                {blog.category}
              </span>
            )}
            <span className="px-2.5 py-1 rounded-full bg-dark-700 text-gray-300 border border-dark-600">
              {blog.status}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-bold text-white leading-tight">{blog.title}</h1>

          {(blog.short_description || blog.summary) && (
            <p className="mt-4 text-gray-300 text-base leading-relaxed">
              {blog.short_description || blog.summary}
            </p>
          )}

          <div className="mt-6 flex flex-wrap gap-4 text-sm text-gray-400">
            <span className="inline-flex items-center gap-1.5">
              <UserIcon className="w-4 h-4" />
              {blog.author || 'AnalyzerView Research Desk'}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <CalendarDaysIcon className="w-4 h-4" />
              {formatDate(blog.published_at || blog.created_at)}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <ClockIcon className="w-4 h-4" />
              {blog.read_time || '5 min read'}
            </span>
          </div>

          {tags.length > 0 && (
            <div className="mt-5 flex flex-wrap gap-2">
              {tags.map(tag => (
                <span key={tag} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs bg-dark-800 border border-dark-600 text-gray-300">
                  <TagIcon className="w-3 h-3" />
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="mt-8 pt-8 border-t border-dark-700 prose prose-invert max-w-none prose-p:text-gray-300 prose-headings:text-white">
            <RichTextContent html={blog.content} />
          </div>
        </div>
      </article>
    </section>
  )
}

export default BlogDetailPage
